/**
 * [2026-09-27] 사이트 알림 다리 — 브라우저 기본 알림창(window.alert)과 확인창을 운영사 로고가 있는 사이트 알림창
 *   (components/common/AlertHost.jsx → 공용 알림창 Modal)으로 보낸다.
 *
 * WHY: 충전·환전·고객센터·"로그인이 필요합니다." 같은 안내 수십 곳이 브라우저 기본 알림창을 써서, 같은 사이트 안에서
 *   어떤 안내는 운영사 로고가 있는 알림창, 어떤 안내는 로고 없이 사이트 주소가 제목으로 뜨는 브라우저 창으로 떴다
 *   (2026-09-27 실측 — 블랙 58·루미나 63·디자인1 66곳). 부르는 곳을 하나씩 고치지 않고, 앱이 시작할 때 한 번
 *   window.alert 를 이 다리로 바꿔 전부 같은 알림창으로 보낸다(규칙의 자리 한 곳).
 *
 * 동작:
 *   - 메시지는 한 번에 하나씩 띄우고, 확인을 누르면 다음 것을 띄운다.
 *   - 알림창 주인(AlertHost)이 붙기 전에 온 메시지는 쌓아 두었다가 붙는 즉시 띄운다.
 *   - 브라우저 기본 알림과 달리 코드 흐름을 멈추지 않는다. 그래서 알림 바로 뒤에 페이지를 새로 여는 곳
 *     (강제 로그아웃·무활동 로그아웃 → 로그인 화면)은, 아직 확인하지 않은 메시지를 페이지를 떠나는 순간(pagehide)
 *     이 탭의 세션 저장소에 맡겼다가 새 페이지에서 다시 띄운다(안 그러면 메시지가 보이기도 전에 사라진다).
 *   - 확인창(예/아니오)은 브라우저 확인창처럼 그 자리에서 답을 돌려줄 수 없어 confirmDialog(문구) 가 약속(Promise)으로 답한다
 *     — 부르는 쪽은 await 로 받는다. 페이지를 떠나면 답하지 않은 확인창은 버린다(되살리지 않는다).
 */

const PENDING_KEY = 'siteAlertPending';
// 페이지를 떠날 때 맡겨 둘 메시지 수 상한(같은 안내가 반복돼도 새 페이지를 뒤덮지 않게)
const MAX_PENDING_SAVED = 10;

let items = []; // 띄울 차례를 기다리는 항목 [{ kind: 'alert'|'confirm', text, resolve? }]
const subscribers = new Set();
let installed = false;
// 알림창이 쓸 사이트 디자인(로고) 정보 — 앱이 불러오는 즉시 넘겨준다(setAlertDesignTemplate).
//   WHY 여기 두나: 알림창 주인은 앱 맨 바깥에 항상 붙어 있어야 해서(아래 AlertHost 설명) 디자인 정보를 화면 안쪽에서 물려받을 수 없다.
let designTemplateForAlerts = null;
const templateSubscribers = new Set();

function emit() {
  const snapshot = items.slice();
  subscribers.forEach((fn) => {
    try { fn(snapshot); } catch (e) { /* 화면 쪽 오류가 다리를 멈추지 않게 한다 */ }
  });
}

function toText(message) {
  return message === undefined || message === null ? '' : String(message);
}

/** 알림 한 건을 줄 세운다(window.alert 가 이것을 부른다). */
export function pushAlert(message) {
  items.push({ kind: 'alert', text: toText(message) });
  emit();
}

/** 확인창 — 확인을 누르면 true, 취소면 false 로 답한다. 부르는 쪽: if (!(await confirmDialog('…'))) return; */
export function confirmDialog(message) {
  return new Promise((resolve) => {
    items.push({ kind: 'confirm', text: toText(message), resolve });
    emit();
  });
}

/**
 * 띄운 항목 하나를 닫는다. 같은 항목을 두 번 닫아도 한 번만 처리한다
 * (공용 알림창은 '확인'을 누르면 확인 처리 뒤 닫기 처리를 잇달아 부르기 때문).
 * @param {object} item 닫을 항목(구독으로 받은 배열 속 그 객체)
 * @param {boolean} answer 확인창의 답(확인 = true)
 */
export function closeItem(item, answer) {
  const idx = items.indexOf(item);
  if (idx === -1) return;
  items.splice(idx, 1);
  if (item.kind === 'confirm' && typeof item.resolve === 'function') item.resolve(answer === true);
  emit();
}

/** 알림창 주인이 붙어 변화를 받는다(붙는 즉시 현재 목록을 한 번 받는다). 해제 함수를 돌려준다. */
export function subscribeAlerts(fn) {
  subscribers.add(fn);
  fn(items.slice());
  return () => { subscribers.delete(fn); };
}

function restorePending() {
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    window.sessionStorage.removeItem(PENDING_KEY);
    const list = raw ? JSON.parse(raw) : [];
    if (Array.isArray(list)) list.forEach((t) => items.push({ kind: 'alert', text: toText(t) }));
  } catch (e) { /* 세션 저장소를 쓸 수 없는 환경 — 맡긴 메시지 없음 */ }
}

function savePending() {
  try {
    const texts = items.filter((it) => it.kind === 'alert').map((it) => it.text).slice(0, MAX_PENDING_SAVED);
    if (texts.length > 0) window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(texts));
  } catch (e) { /* 무시 */ }
}

function dropSavedCopy(event) {
  // 뒤로 가기 캐시로 같은 페이지가 되살아나면 메시지가 메모리에 그대로 있으므로, 맡겨 둔 사본은 버린다(두 번 뜨지 않게)
  if (event && event.persisted) {
    try { window.sessionStorage.removeItem(PENDING_KEY); } catch (e) { /* 무시 */ }
  }
}

/** 앱이 사이트 디자인 정보를 불러오면(바뀌면) 넘겨준다 — 알림창 로고에 쓴다. */
export function setAlertDesignTemplate(template) {
  designTemplateForAlerts = template || null;
  templateSubscribers.forEach((fn) => {
    try { fn(designTemplateForAlerts); } catch (e) { /* 무시 */ }
  });
}

/** 알림창 주인이 디자인 정보 변화를 받는다(붙는 즉시 현재 값을 한 번 받는다). 해제 함수를 돌려준다. */
export function subscribeAlertDesignTemplate(fn) {
  templateSubscribers.add(fn);
  fn(designTemplateForAlerts);
  return () => { templateSubscribers.delete(fn); };
}

/** 앱 시작 때 한 번 부른다 — window.alert 를 사이트 알림창으로 돌리고, 지난 페이지에서 맡긴 메시지를 되살린다. */
export function installAlertBridge() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  restorePending();
  window.alert = (message) => { pushAlert(message); };
  window.addEventListener('pagehide', savePending);
  window.addEventListener('pageshow', dropSavedCopy);
}

/** 시험 전용 — 모듈 상태를 처음으로 되돌린다. */
export function _resetForTest() {
  items = [];
  subscribers.clear();
  installed = false;
  designTemplateForAlerts = null;
  templateSubscribers.clear();
}
